const CSRF_COOKIE_NAME = "vardic_csrf";
const CSRF_PROTECTED_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function readCookieValue(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const target = `${name}=`;
  for (const cookiePart of document.cookie.split(";")) {
    const trimmed = cookiePart.trim();
    if (trimmed.startsWith(target)) {
      const value = trimmed.slice(target.length);
      return value ? decodeURIComponent(value) : null;
    }
  }

  return null;
}

export function getCsrfTokenFromCookie(): string | null {
  return readCookieValue(CSRF_COOKIE_NAME);
}

// pirx-i9s6p: in a deployed (cross-origin) stack the CSRF cookie is
// host-only on the API origin, so the frontend origin's `document.cookie`
// never contains it even with `httpOnly: false` + `SameSite=None` (the
// cookie still travels with `credentials: "include"` fetches, so the
// server-side comparison in `middleware/auth.ts` keeps working). The
// session-establishment endpoint therefore returns `csrfToken` explicitly
// (backend `routes/auth.ts`) and we pin it in `sessionStorage` — the
// browser session's lifetime matches the signed session cookie's — so
// protected writes can send the header without reading the cookie.
const CSRF_TOKEN_STORAGE_KEY = "vardic_csrf_token";

function readStoredCsrfToken(): string | null {
  try {
    return sessionStorage.getItem(CSRF_TOKEN_STORAGE_KEY);
  } catch {
    // storage-blocked context (private mode, partitioned storage): fall
    // through to the cookie.
    return null;
  }
}

/** Retain the CSRF token delivered at session establishment. */
export function storeCsrfToken(token: string): void {
  try {
    sessionStorage.setItem(CSRF_TOKEN_STORAGE_KEY, token);
  } catch {
    // no-op in storage-blocked contexts; the cookie path still applies.
  }
}

/** Drop the retained CSRF token (called on sign-out). */
export function clearCsrfToken(): void {
  try {
    sessionStorage.removeItem(CSRF_TOKEN_STORAGE_KEY);
  } catch {
    // no-op in storage-blocked contexts.
  }
}

/**
 * The CSRF token to send in the `x-csrf-token` header: the explicitly
 * delivered token first (cross-origin deployments), falling back to the
 * readable cookie (same-origin / local dev where the cookie is visible to
 * `document.cookie`).
 */
export function getCsrfToken(): string | null {
  return readStoredCsrfToken() ?? getCsrfTokenFromCookie();
}

export function hasCsrfCookie(): boolean {
  return Boolean(getCsrfToken());
}

export function isCsrfProtectedMethod(method: string): boolean {
  return CSRF_PROTECTED_METHODS.has(method.toUpperCase());
}
